import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronDown, Plus, Check } from "lucide-react";
import { apiRequest } from '@/lib/queryClient';
import { useAlert } from '@/hooks/use-alert';
import { usePermissions } from '@/hooks/use-permissions';
import { useDateFormat } from '@/utils/date-formatter';
import { useProjectCurrency } from '@/hooks/use-project-currency';
import { useToast } from '@/hooks/use-toast';
import { RightSidebar, SidebarButtonGroup, SidebarIconButton } from '@/components/ui/right-sidebar';
import { IconButtonFilter } from '@/components/ui/icon-buttons';
import iconShareSvg from '@/assets/web_share.svg';
import iconDeleteSvg from '@/assets/circle_delete.svg';
import { TicketExportOptionsModal } from './TicketExportOptionsModal';
import type { TicketExportDataTicketsTab } from '../services/TicketPDFExportServiceTicketsTab';
import type { ExportOptions } from '../services/TicketPDFExportService';
import { TicketSelectionCheckbox } from './TicketSelectionCheckbox';
import { TicketSelectAllCheckbox } from './TicketSelectAllCheckbox';
import { TicketFilters } from './TicketFilters';
import { TicketWeatherBadge } from '@/components/ticket-weather-badge';
import { useTicketFilters } from '../hooks/useTicketFilters';
import { useTicketSelection } from '../hooks/useTicketSelection';
import { QUERY_KEYS } from '../utils/project-constants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ProjectTicketsTabProps {
  projectId: string;
  project: any;
  tickets: any[];
  plans: any[];
  /** When set, only tickets for this plan are shown. Empty string = all plans. */
  selectedPlanLevelId?: string;
  teamMembers: any[];
  statusList: any[];
  priorities: any[];
  gewerke: any[];
  isLoading?: boolean;
  onOpenEditModal: (ticket: any) => void;
  onOpenNewTicketModal: () => void;
  onSelectionChange?: (hasSelection: boolean, getSelectedTicketsData: () => any[]) => void;
  /** Transform tickets for PDF export (mobile-matching layout) */
  onTransformTicketsForExport?: (tickets: any[], options: ExportOptions) => Promise<TicketExportDataTicketsTab[]>;
}

export const ProjectTicketsTab: React.FC<ProjectTicketsTabProps> = ({
  projectId,
  project,
  tickets,
  plans,
  selectedPlanLevelId = '',
  teamMembers,
  statusList,
  priorities,
  gewerke,
  isLoading = false,
  onOpenEditModal,
  onOpenNewTicketModal,
  onSelectionChange,
  onTransformTicketsForExport,
}) => {
  const { t, i18n } = useTranslation(['pages.projectDetails', 'components.ticket', 'common', 'pages.dashboard']);
  const { confirm } = useAlert();
  const { toast } = useToast();
  const { canDeleteTickets: canDeleteTicketsFromHook, hasPermission, canExportData } = usePermissions();
  const queryClient = useQueryClient();
  const { dateFormat, timeZone, formatDate } = useDateFormat();
  const currency = useProjectCurrency(projectId);
  
  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Deleting tickets follows project manage scope.
  const canDeleteTickets = () => {
    return canDeleteTicketsFromHook() || hasPermission('manage_projects') || hasPermission('manage_all_projects');
  };

  // Apply header plan level filter first (selected plan or all)
  const ticketsByPlan = useMemo(() => {
    if (!selectedPlanLevelId) return tickets;
    return tickets.filter((t: any) => t.planLocation?.planId === selectedPlanLevelId);
  }, [tickets, selectedPlanLevelId]);

  // Filter state
  const [showTicketFilters, setShowTicketFilters] = useState(false);
  const { filters, updateFilter, resetFilters, filteredTickets } = useTicketFilters(ticketsByPlan, plans);

  // Sort state
  const [sortField, setSortField] = useState<string>('ticketNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Delete state
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  // Helper to get contractor name - prefers ticket's own assignedContractorName, falls back to lookup
  const getContractorName = useCallback((ticket: any) => {
    // First, use the assignedContractorName from the ticket data (populated by API JOIN)
    if (ticket.assignedContractorName) {
      return ticket.assignedContractorName;
    }
    // Fallback: lookup by assignedContractorId in teamMembers
    const assignedContractorId = ticket.assignedContractorId;
    if (!assignedContractorId) return '-';
    const contractor = teamMembers.find((m: any) => m.user_id === assignedContractorId || m.id === assignedContractorId);
    return contractor?.display_name || contractor?.email || '-';
  }, [teamMembers]);

  // Helper to get status display name
  function getStatusDisplayName(status: string | null | undefined, statusId?: string | null) {
    if (statusId && statusList.length > 0) {
      const statusObj = statusList.find((s: any) => s.id === statusId);
      if (statusObj) {
        return statusObj.displayName || statusObj.name;
      }
      return '-';
    }
    if (status && statusList.length > 0) {
      const normalized = status.toLowerCase().trim();
      const statusObj = statusList.find((s: any) =>
        s.name.toLowerCase().trim() === normalized ||
        (s.displayName || '').toLowerCase().trim() === normalized
      );
      if (statusObj) {
        return statusObj.displayName || statusObj.name;
      }
      return '-';
    }
    return status || '-';
  }

  // Helper to get gewerk display name
  function getGewerkDisplayName(gewerk: string | null | undefined, gewerkId?: string | null) {
    if (!gewerk && !gewerkId) return '-';
    if (gewerkId && gewerke.length > 0) {
      const gewerkObj = gewerke.find((g: any) => g.id === gewerkId);
      if (gewerkObj) {
        return gewerkObj.displayName || gewerkObj.name;
      }
      return '-';
    }
    if (gewerk && gewerke.length > 0) {
      const gewerkObj = gewerke.find((g: any) => g.name.toLowerCase().trim() === gewerk.toLowerCase().trim());
      if (gewerkObj) {
        return gewerkObj.displayName || gewerkObj.name;
      }
      return '-';
    }
    return gewerk || '-';
  }

  // Priority helpers
  function getPriorityInfo(ticket: any) {
    if (ticket.priorityId && priorities.length > 0) {
      const priority = priorities.find((p: any) => p.id === ticket.priorityId);
      if (priority) {
        return {
          name: priority.displayName || priority.name,
          color: priority.color || '#6b7280',
        };
      }
      if (ticket.priorityId) {
        return { name: '-', color: '#6b7280' };
      }
    }
    const raw = ticket.priority || ticket.prioritaet || '';
    if (raw) {
      if (priorities.length > 0) {
        const priority = priorities.find((p: any) => p.name.toLowerCase() === raw.toLowerCase());
        if (priority) {
          return {
            name: priority.displayName || priority.name,
            color: priority.color || '#6b7280',
          };
        }
      }
      return { name: raw, color: '#6b7280' };
    }
    return { name: '-', color: '#6b7280' };
  }

  // Sort value helper
  const getSortValue = useCallback((ticket: any, field: string) => {
    switch (field) {
      case 'ticketNumber':
        return parseInt(ticket.ticketNumber) || 0;
      case 'title':
        return ticket.title || '';
      case 'planLevel': {
        const planLocation = ticket.planLocation || ticket.plan_location;
        if (planLocation?.planId) {
          const plan = plans.find((p: any) => p.id === planLocation.planId);
          return plan?.name || plan?.fileName || '';
        }
        return '';
      }
      case 'status':
        return getStatusDisplayName(ticket.status, ticket.statusId);
      case 'priority':
        return getPriorityInfo(ticket).name || '';
      case 'trade':
        return getGewerkDisplayName(ticket.gewerk, ticket.gewerkId);
      case 'location':
        return ticket.location || '';
      case 'assigned':
        return getContractorName(ticket);
      case 'due':
        return ticket.dueDate ? new Date(ticket.dueDate).getTime() : 0;
      case 'cost': {
        const numValue = parseFloat(String(ticket.costs || '').replace(/[^\d,.-]/g, '').replace(',', '.'));
        return isNaN(numValue) ? 0 : numValue;
      }
      case 'progress':
        return ticket.progress ?? 0;
      case 'weather':
        return ticket.weatherData?.conditions || ticket.weather?.conditions || '';
      case 'overtime':
        return ticket.overtimeNeeded ? (ticket.overtimeConfirmed ? 2 : 1) : 0;
      default:
        return ticket[field] ?? '';
    }
  }, [getContractorName, plans, statusList, gewerke, priorities]);

  // Sorted tickets
  const sortedTickets = useMemo(() => {
    return [...filteredTickets].sort((a, b) => {
      const aValue = getSortValue(a, sortField);
      const bValue = getSortValue(b, sortField);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aText = String(aValue || '').toLowerCase();
      const bText = String(bValue || '').toLowerCase();
      return sortDirection === 'asc'
        ? aText.localeCompare(bText)
        : bText.localeCompare(aText);
    });
  }, [filteredTickets, getSortValue, sortDirection, sortField]);

  // Ticket selection
  const {
    selectedTickets,
    isAllSelected,
    isIndeterminate,
    toggleTicket,
    toggleAllTickets,
    clearSelection,
    getSelectedTicketsData,
    hasSelection
  } = useTicketSelection(sortedTickets);

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(hasSelection, getSelectedTicketsData);
    }
  }, [hasSelection, getSelectedTicketsData, onSelectionChange]);

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortableHeader = (label: string, field: string, className = '') => (
    <th
      className={`px-3 py-3 text-left cursor-pointer hover:bg-slate-50 whitespace-nowrap overflow-hidden text-ellipsis ${className}`}
      onClick={() => handleSort(field)}
      style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}
    >
      <div className="flex items-center space-x-1">
        <span className="truncate">{label}</span>
        {sortField === field ? (
          sortDirection === 'asc' ? <ChevronUp className="h-3 w-3 flex-shrink-0" /> : <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : null}
      </div>
    </th>
  );

  // Delete selected tickets
  const handleDeleteSelectedTickets = useCallback(async () => {
    if (selectedTickets.size === 0 || isDeletingSelected) {
      return;
    }
    const confirmed = await confirm({
      message: t('pages.projectDetails:tickets.deleteSelectedConfirm', { count: selectedTickets.size }),
      variant: 'destructive',
    });
    if (!confirmed) {
      return;
    }
    const ticketIds = Array.from(selectedTickets);
    try {
      setIsDeletingSelected(true);
      for (const ticketId of ticketIds) {
        const response = await apiRequest(`/api/tickets/${ticketId}`, {
          method: 'DELETE',
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to delete ticket ${ticketId}`);
        }
      }
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.projectTickets(projectId)
      });
      clearSelection();
    } catch (error: any) {
      console.error('Failed to delete selected tickets:', error);
    } finally {
      setIsDeletingSelected(false);
    }
  }, [confirm, selectedTickets, t, isDeletingSelected, queryClient, projectId, clearSelection]);

  // Share handler (placeholder)
  const handleShareSelected = useCallback(() => {
    if (selectedTickets.size === 0) return;
    // Future: open share dialog
  }, [selectedTickets.size]);

  // Export handler - opens modal
  const handleExportClick = useCallback(() => {
    if (selectedTickets.size === 0) return;
    setIsExportModalOpen(true);
  }, [selectedTickets.size]);

  // Handle actual export after modal confirmation
  const handleExport = useCallback(async (options: ExportOptions) => {
    if (selectedTickets.size === 0 || !onTransformTicketsForExport) return;

    try {
      setIsExporting(true);
      
      // Get selected tickets data
      const ticketsToExport = getSelectedTicketsData();
      
      // Transform tickets with extended data for mobile-matching layout
      const transformedTickets = await onTransformTicketsForExport(ticketsToExport, options);
      
      // Lazy-load the PDF service
      const { TicketPDFExportServiceTicketsTab } = await import('../services/TicketPDFExportServiceTicketsTab');
      const exportService = new TicketPDFExportServiceTicketsTab(i18n.language, {
        dateFormat,
        timeZone,
      });
      await exportService.exportTickets(transformedTickets, project?.name || 'Project', options);
      
      // Success toast
      toast({
        title: t('common:success'),
        description: t('tickets.exportSuccess', { count: transformedTickets.length }),
      });
      
    } catch (error) {
      // Error toast
      toast({
        title: t('common:error'),
        description: t('tickets.exportError'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [selectedTickets.size, onTransformTicketsForExport, getSelectedTicketsData, i18n.language, project?.name, toast, t, dateFormat, timeZone]);

  // Check if filters are active
  const hasActiveFilters =
    filters.status.length > 0 ||
    filters.priority.length > 0 ||
    filters.trade.length > 0 ||
    filters.planLevel.length > 0 ||
    filters.assigned.length > 0;

  if (isLoading) {
    return (
      <div className="p-4 pt-2 -mt-[8px]">
        <div className="bg-[#DDDDDD] rounded-[20px] p-[32px]">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-300">
                <thead>
                  <tr className="list-table-header">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider w-12"></th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.number')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.title')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.planLevel')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.status')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.priority')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.trade')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.location')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.assigned')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.due')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.cost')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.progress')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('tickets.table.weather')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">{t('pages.dashboard:table.headers.overtime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(8)].map((_, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-3 py-3"><div className="h-4 w-4 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-12 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-48 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-20 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-32 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-28 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-20 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-16 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-12 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-16 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-20 bg-slate-200 rounded animate-pulse"></div></td>
                      <td className="px-3 py-3"><div className="h-4 w-16 bg-slate-200 rounded animate-pulse"></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pt-2 -mt-[8px] h-full flex flex-col min-h-0">
      <div className="flex gap-4 max-h-full min-h-0">
        <div className="flex-1 flex flex-col min-h-0 min-w-0 max-h-full">
          <div className="bg-[#DDDDDD] rounded-[20px] p-[32px] flex flex-col min-h-0 min-w-0 max-h-full">
            {tickets.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-slate-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No tickets yet</h3>
                <p className="text-slate-600">Get started by creating your first ticket for this project.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none flex flex-col min-h-0 min-w-0 max-h-full">
                <div className="overflow-auto min-h-0 min-w-0">
                  <table className="min-w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-3 text-left w-12" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '16px', fontWeight: 'bold', color: '#000000' }}>
                          <TicketSelectAllCheckbox
                            isAllSelected={isAllSelected}
                            isIndeterminate={isIndeterminate}
                            onToggleAll={toggleAllTickets}
                          />
                        </th>
                        {renderSortableHeader(t('tickets.table.number'), 'ticketNumber')}
                        {renderSortableHeader(t('tickets.table.title'), 'title')}
                        {renderSortableHeader(t('tickets.table.planLevel'), 'planLevel', 'max-w-[140px]')}
                        {renderSortableHeader(t('tickets.table.status'), 'status')}
                        {renderSortableHeader(t('tickets.table.priority'), 'priority')}
                        {renderSortableHeader(t('tickets.table.trade'), 'trade')}
                        {renderSortableHeader(t('tickets.table.location'), 'location')}
                        {renderSortableHeader(t('tickets.table.assigned'), 'assigned')}
                        {renderSortableHeader(t('tickets.table.due'), 'due')}
                        {renderSortableHeader(t('tickets.table.cost'), 'cost')}
                        {renderSortableHeader(t('tickets.table.progress'), 'progress')}
                        {renderSortableHeader(t('tickets.table.weather'), 'weather')}
                        {renderSortableHeader(t('pages.dashboard:table.headers.overtime'), 'overtime')}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTickets.map((ticket: any) => (
                        <tr
                          key={ticket.id}
                          className="border-b border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => onOpenEditModal(ticket)}
                          style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
                        >
                          <td className="px-3 py-0.5 whitespace-nowrap w-12" onClick={(e) => e.stopPropagation()}>
                            <TicketSelectionCheckbox
                              ticketId={ticket.id}
                              isSelected={selectedTickets.has(ticket.id)}
                              onToggle={toggleTicket}
                            />
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            #{ticket.ticketNumber}
                          </td>
                          <td className="px-3 py-0.5 max-w-xs" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            <div className="truncate" title={ticket.title}>
                              {ticket.title}
                            </div>
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap max-w-[140px] overflow-hidden text-ellipsis" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {(() => {
                              const planLocation = ticket.planLocation || (ticket as any).plan_location;
                              if (planLocation?.planId) {
                                const plan = plans.find((p: any) => p.id === planLocation.planId);
                                return plan?.name || plan?.fileName || 'Plan';
                              }
                              return '-';
                            })()}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap">
                            <span
                              style={{
                                fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontSize: '14px',
                                fontWeight: 'normal',
                                color: '#000000',
                              }}
                            >
                              {getStatusDisplayName(ticket.status, (ticket as any).statusId)}
                            </span>
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap">
                            {(() => {
                              const priorityInfo = getPriorityInfo(ticket);
                              return (
                                <span
                                  style={{
                                    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    color: '#000000',
                                  }}
                                >
                                  {priorityInfo.name}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {getGewerkDisplayName(ticket.gewerk, (ticket as any).gewerkId)}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {ticket.location || '-'}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {getContractorName(ticket)}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {ticket.dueDate ? formatDate(ticket.dueDate) : '-'}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {ticket.costs ? (() => {
                              const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
                              const numValue = parseFloat(String(ticket.costs).replace(/[^\d,.-]/g, '').replace(',', '.'));
                              if (isNaN(numValue)) return ticket.costs;
                              return new Intl.NumberFormat(locale, {
                                style: 'currency',
                                currency: currency || 'EUR',
                                minimumFractionDigits: numValue % 1 !== 0 ? 1 : 0,
                                maximumFractionDigits: 1
                              }).format(numValue);
                            })() : '-'}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap">
                            {ticket.progress !== undefined ? (
                              <span style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                                %{ticket.progress}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            <TicketWeatherBadge weatherConditions={ticket.weatherConditions} />
                          </td>
                          <td className="px-3 py-0.5 whitespace-nowrap" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', fontWeight: 'normal', color: '#000000' }}>
                            {(() => {
                              const overtimeNeeded = ticket.overtimeNeeded;
                              const overtimeHours = ticket.overtimeHours;
                              const overtimeConfirmed = ticket.overtimeConfirmed;
                              if (!overtimeNeeded) {
                                return <span className="text-xs text-slate-500">-</span>;
                              }
                              return (
                                <span className="inline-flex items-center gap-1">
                                  <span className="text-sm font-medium">{overtimeHours}h</span>
                                  {overtimeConfirmed && (
                                    <Check className="h-4 w-4 text-green-600" />
                                  )}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Export/Share, Filter, Delete */}
        <RightSidebar>
          {/* Export Button (uses Share icon) */}
          <SidebarButtonGroup label={t('tickets.exportButton.label', { count: selectedTickets.size })} hideLabel>
            <SidebarIconButton
              icon={iconShareSvg}
              alt="Export"
              onClick={canExportData() && onTransformTicketsForExport ? handleExportClick : handleShareSelected}
              disabled={selectedTickets.size === 0 || isExporting}
              isLoading={isExporting}
              title={selectedTickets.size === 0 
                ? t('tickets.exportButton.selectHint')
                : t('tickets.exportButton.exportCount', { count: selectedTickets.size })
              }
            />
          </SidebarButtonGroup>
          <SidebarButtonGroup label={t('plans.filter', 'Filter')} hideLabel>
            <Popover open={showTicketFilters} onOpenChange={setShowTicketFilters}>
              <PopoverTrigger asChild>
                <span className="relative inline-block">
                  <IconButtonFilter
                    size={36}
                    title={t('filters.tickets')}
                  />
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full" />
                  )}
                </span>
              </PopoverTrigger>
              <PopoverContent
                className="w-80 h-[560px] max-h-[calc(100vh-120px)] p-0 overflow-hidden bg-[#E8E8E8] rounded-2xl border-0 shadow-md"
                side="left"
                align="center"
                sideOffset={60}
              >
                <TicketFilters
                  showFilters={true}
                  onToggleFilters={() => setShowTicketFilters(false)}
                  filters={filters}
                  onFilterChange={updateFilter}
                  onResetFilters={resetFilters}
                  gewerke={gewerke}
                  plans={plans}
                  teamMembers={teamMembers}
                  statusData={statusList}
                  priorityData={priorities}
                  hideButton={true}
                  embedded={true}
                />
              </PopoverContent>
            </Popover>
          </SidebarButtonGroup>
          <SidebarButtonGroup label={t('common:delete')} hideLabel>
            <SidebarIconButton
              icon={iconDeleteSvg}
              alt="Delete"
              onClick={handleDeleteSelectedTickets}
              disabled={!canDeleteTickets() || selectedTickets.size === 0}
              isLoading={isDeletingSelected}
              title={t('pages.projectDetails:tickets.deleteSelected')}
            />
          </SidebarButtonGroup>
        </RightSidebar>
        
        {/* Export Options Modal */}
        <TicketExportOptionsModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
        />
      </div>
    </div>
  );
};
